import React, { useState, useMemo } from 'react';
import '../../../../../../../Stylesheets/Dashboard/Tabs/Materials/MaterialInventoryTable.css';

const ITEMS_PER_PAGE = 5;

const MaterialInventoryTable = ({
  materials = [],
  projectId,
  onUploadBill,
  onIssueMaterial,
  onLogUsage,
  onViewItem,
  onDelete
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [sortAsc, setSortAsc] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [openMenuFor, setOpenMenuFor] = useState(null);

  const handleSortToggle = () => setSortAsc(prev => !prev);

  const filteredMaterials = useMemo(() => {
    return materials
      .filter(mat =>
        mat.name?.toLowerCase().includes(searchQuery.toLowerCase())
      )
      .sort((a, b) => {
        if (a.hasUnbilledReceives && !b.hasUnbilledReceives) return -1;
        if (!a.hasUnbilledReceives && b.hasUnbilledReceives) return 1;
        return sortAsc
          ? a.name.localeCompare(b.name)
          : b.name.localeCompare(a.name);
      });
  }, [materials, searchQuery, sortAsc]);

  const paginatedMaterials = useMemo(() => {
    const startIdx = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredMaterials.slice(startIdx, startIdx + ITEMS_PER_PAGE);
  }, [filteredMaterials, currentPage]);

  const totalPages = Math.ceil(filteredMaterials.length / ITEMS_PER_PAGE);

  const getInStock = (mat) => {
    const received = Number(mat.received) || 0;
    const used = Number(mat.used) || 0;
    return received - used;
  };

  const totalInventoryValue = useMemo(() => {
    return materials.reduce((sum, mat) => {
      const qty = Number(mat.received) || 0;
      const rate = Number(mat.rate) || 0;
      return sum + (qty * rate);
    }, 0);
  }, [materials]);

  const renderActions = (mat) => {
    const inStock = getInStock(mat) > 0;
    const hasUnbilledReceives = mat.hasUnbilledReceives;





    return (
      <>
        {hasUnbilledReceives && (
          <button
            className="material-action-btn orange"
            title="Upload Bill for this received material"
            onClick={() =>
              onUploadBill({
                ...mat,
                skipQuantityInBill: true,
                quantityToBill: mat.unbilledQuantity || 0,
              })
            }
          >
            Upload Bill
          </button>
        )}
        {inStock && (
          <button
            className="material-action-btn dark"
            title="Log how much material was used"
            onClick={() => onLogUsage(mat)}
          >
            Log Usage
          </button>
        )}
        <button
          className="material-action-btn grey"
          title="View full item history"
          onClick={() => onViewItem(mat)}
        >
          View Item
        </button>
      </>
    );
  };

  const getVendorDisplay = (mat) => {
    return mat.hasUnbilledReceives
      ? <span className="pending-bill-warning">⚠ Pending Bill</span>
      : (mat.vendor || '-');
  };

  const getLastUpdated = (mat) => {
    const date = mat.lastUpdated || mat.updatedAt;
    return date ? new Date(date).toLocaleDateString('en-IN') : '-';
  };

  // Inline kebab menu for desktop table rows
  const RowKebab = ({ mat }) => {
    const btnRef = React.useRef(null);
    const menuRef = React.useRef(null);
    const hasUnbilledReceives = !!mat.hasUnbilledReceives;
    const isOpen = openMenuFor === mat._id;

    React.useEffect(() => {
      const onDocClick = (e) => {
        if (!isOpen) return;
        if (
          btnRef.current && btnRef.current.contains(e.target)
        ) return;
        if (
          menuRef.current && menuRef.current.contains(e.target)
        ) return;
        setOpenMenuFor(null);
      };
      document.addEventListener('mousedown', onDocClick);
      return () => document.removeEventListener('mousedown', onDocClick);
    }, [isOpen]);

    return (
      <div className="mat-kebab-wrapper">
        <button
          ref={btnRef}
          className="mat-kebab-trigger"
          aria-haspopup="menu"
          aria-expanded={isOpen}
          title="More actions"
          onClick={() =>
            setOpenMenuFor((prev) => (prev === mat._id ? null : mat._id))
          }
        >
          ⋮
        </button>
        {isOpen && (
          <div ref={menuRef} className="mat-kebab-menu" role="menu">
            <button
              role="menuitem"
              className="mat-kebab-item"
              onClick={() => {
                setOpenMenuFor(null);
                onViewItem(mat);
              }}
            >
              View Item
            </button>
            <button
              role="menuitem"
              className="mat-kebab-item"
              onClick={() => {
                setOpenMenuFor(null);
                onLogUsage(mat);
              }}
              disabled={getInStock(mat) <= 0}
              title={getInStock(mat) <= 0 ? 'No stock to log usage' : 'Log Usage'}
            >
              Log Usage
            </button>
            <button
              role="menuitem"
              className="mat-kebab-item"
              onClick={() => {
                setOpenMenuFor(null);
                onUploadBill({
                  ...mat,
                  skipQuantityInBill: true,
                  quantityToBill: mat.unbilledQuantity || 0,
                });
              }}
              disabled={!hasUnbilledReceives}
              title={hasUnbilledReceives ? 'Upload Bill' : 'No pending bill'}
            >
              Upload Bill
            </button>
            <div className="mat-kebab-sep" />
            <button
              role="menuitem"
              className="mat-kebab-item"
              onClick={() => {
                setOpenMenuFor(null);
                onDelete && onDelete(mat); // calls soft delete
              }}
              disabled={hasUnbilledReceives}  /* keep rule or remove if you relaxed backend */
              title={hasUnbilledReceives ? 'Resolve pending bills first' : 'Delete'}
            >
              Delete
            </button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="material-tab-table-wrapper">
      <div className="material-tab-table-header">
        <h3>Inventory</h3>
        <div className="material-tab-table-controls">
          <input
            type="text"
            placeholder="Search Material"
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setCurrentPage(1);
            }}
          />
          <button onClick={handleSortToggle}>
            Sort by {sortAsc ? 'Name ▲' : 'Name ▼'}
          </button>
        </div>
      </div>



      {/* Desktop Table */}
      <table className="material-tab-table desktop-table">
        <thead>
          <tr>
            <th>Material</th>
            <th>Received</th>
            <th>Used</th>
            <th>In Stock</th>
            <th>Vendor</th>
            <th>Last Update</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {paginatedMaterials.length === 0 ? (
            <tr>
              <td colSpan="7" className="material-tab-table-empty">
                No materials found
              </td>
            </tr>
          ) : (
            paginatedMaterials.map((mat) => (
              <tr
                key={mat._id}
                className={mat.hasUnbilledReceives ? 'pending-row' : ''}
              >
                <td>{mat.name}</td>
                <td>{mat.received || 0} {mat.unit || ''}</td>
                <td>{mat.used || 0} {mat.unit || ''}</td>
                <td>{getInStock(mat)} {mat.unit || ''}</td>
                <td>{getVendorDisplay(mat)}</td>
                <td>{getLastUpdated(mat)}</td>

                <td className="mat-action-cell"><RowKebab mat={mat} /></td>
              </tr>
            ))
          )}
        </tbody>
        {/* Summary row inside table */}
        <tfoot>
          <tr className="table-summary-row">
            {/* Empty cells to align with your 7 columns */}
            <td colSpan="5"></td>
            <th scope="row" className="table-summary-label">Total Value</th>
            <td className="table-summary-amount">
              ₹{totalInventoryValue.toLocaleString()}
            </td>
          </tr>
        </tfoot>
      </table>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="material-pagination">
          <button disabled={currentPage === 1} onClick={() => setCurrentPage(prev => prev - 1)}>Prev</button>
          <span>Page {currentPage} of {totalPages}</span>
          <button disabled={currentPage === totalPages} onClick={() => setCurrentPage(prev => prev + 1)}>Next</button>
        </div>
      )}

      {/* Mobile Cards */}
      <div className="material-mobile-cards">
        {paginatedMaterials.map((mat) => (
          <div
            key={mat._id}
            className={`material-card ${mat.hasUnbilledReceives ? 'pending-row' : ''}`}
          >
            <div className="material-card-header">
              <h4>{mat.name}</h4>
              <small>{getVendorDisplay(mat)}</small>
            </div>
            <div className="material-card-details">
              <p><strong>Received:</strong> {mat.received || 0} {mat.unit || ''}</p>
              <p><strong>Used:</strong> {mat.used || 0} {mat.unit || ''}</p>
              <p><strong>In Stock:</strong> {getInStock(mat)} {mat.unit || ''}</p>
              <p><strong>Last Update:</strong> {getLastUpdated(mat)}</p>
            </div>
            <div className="material-card-actions">
              {renderActions(mat)}
            </div>
          </div>
        ))}
        {paginatedMaterials.length > 0 && (
          <div className="material-mobile-summary">
            <span>Total Value</span>
            <strong>₹{totalInventoryValue.toLocaleString()}</strong>
          </div>
        )}
      </div>
    </div>
  );
};

export default MaterialInventoryTable;
